import { useApolloClient } from '@apollo/client';
import { Question } from '@jupiterone/jupiter-types/question';
import pick from 'lodash.pick';
import { useEffect } from 'react';
import { useArrayState } from 'react-use-object-state';
import { removeDeep } from '../../..';
import { ListStandards } from '../../../graphql/queries';
import {
  useCreateQuestion,
  useDeleteQuestion,
  useUpdateQuestion,
} from '../../../questions';
import stripTypeName from '../../util/stripTypeName';
import {
  useMockHook,
  mockFunction,
} from '@jupiterone/jupiter-web-toolkit/src/util/useMockHook';

// remove me after backend fix. Will no longer need sanitization.
const sanitizeQuestion = (question: Question): Question => {
  const questionCopy = removeDeep(question, (val: any) => val === null);

  if (!questionCopy.description) {
    questionCopy.description = '';
  }

  question.queries?.forEach((query, index) => {
    delete questionCopy.queries[index].lastResult;
    if (!query.name) {
      delete questionCopy.queries[index].name;
    }
  });
  // TODO: Generate types for the `mutate` calls from
  // the graphQL schema instead of relying on shared types.

  // lastUpdatedTimestamp, although it exists on the type
  // is not a valid input / argument for the mutations.
  delete questionCopy.lastUpdatedTimestamp;
  return questionCopy;
};

export type QuestionModalVariant =
  | 'save'
  | 'edit'
  | 'clone'
  | 'remove'
  | 'create';

export interface HookProps {
  variant?: QuestionModalVariant;
  open: boolean;
  onClose: VoidFunction;
  onSubmit?: (submittedQuestion: Question) => void;
  refetchQuestions?: VoidFunction;
}

const useVariantQuestionModalLogic = ({
  variant,
  open,
  onClose,
  onSubmit,
  refetchQuestions,
}: HookProps) => {
  const client = useApolloClient();
  const complianceStandards = useArrayState<string>([]);

  useEffect(() => {
    const fetchComplianceStandards = async () => {
      const data = await client.query({
        query: ListStandards,
        variables: {
          size: 50,
        },
        fetchPolicy: 'cache-first',
      });
      const complianceStandardsData =
        data.data?.complianceStandards?.complianceStandards || [];
      if (complianceStandardsData.length > 0) {
        const complianceStandardsNames: string[] = [];
        complianceStandardsData.forEach((complianceStandard: any) => {
          const complianceStandardName =
            complianceStandard.specification.standard;
          // only get unique standards
          if (!complianceStandardsNames.includes(complianceStandardName)) {
            complianceStandardsNames.push(complianceStandardName);
          }
        });
        complianceStandards.setState(complianceStandardsNames);
      }
    };
    if (open) {
      void fetchComplianceStandards();
    }
  }, [open]);

  const createQuestion = useCreateQuestion();
  const handleSaveQuestion = async (question: Question) => {
    const sanitizedQuestion = sanitizeQuestion(question);
    delete (sanitizedQuestion as any).id;

    const response = await createQuestion({
      variables: {
        question: sanitizedQuestion,
      },
    });
    if (response?.data) {
      refetchQuestions?.();
      onSubmit?.(response.data);
      onClose();
    }
  };

  const editQuestion = useUpdateQuestion();
  const handleEditQuestion = async (question: Question) => {
    const response = await editQuestion({
      variables: {
        question,
      },
    });
    if (response?.questionUpdate) {
      onSubmit?.(response?.questionUpdate);
      onClose();
    }
  };

  const deleteQuestion = useDeleteQuestion();
  const handleRemoveQuestion = async (question: Question) => {
    await deleteQuestion({
      variables: {
        id: question.id,
      },
    });

    refetchQuestions?.();
    onSubmit?.(question);
    onClose();
  };

  const handleCloneQuestion = async (question: Question) => {
    const sanitizedQuestion = sanitizeQuestion(question);

    const coreContent = pick(sanitizedQuestion, [
      'title',
      'description',
      'queries',
      'showTrend',
      'pollingInterval',
      'compliance',
      'tags',
    ]);

    const newQuestion = stripTypeName(coreContent);

    const response = await createQuestion({
      variables: { question: newQuestion },
    });
    if (response?.data) {
      onSubmit?.(response.data);
      onClose();
    }
  };

  const variantProps = () => {
    if (variant === 'save') {
      return {
        successText: 'Successfully saved question!',
        busyText: 'Saving question...',
        defaultText: 'Save question',
        buttonText: 'Save',
        disabled: false,
        isInitialValid: false,
        onSubmitQuestion: handleSaveQuestion,
      };
    } else if (variant === 'edit') {
      return {
        successText: 'Successfully edited question!',
        busyText: 'Saving edited question...',
        defaultText: 'Edit question',
        buttonText: 'Save',
        disabled: false,
        isInitialValid: true,
        onSubmitQuestion: handleEditQuestion,
      };
    } else if (variant === 'clone') {
      return {
        successText: 'Successfully cloned question!',
        busyText: 'Cloning question...',
        defaultText: 'Clone question',
        buttonText: 'Clone',
        disabled: false,
        isInitialValid: true,
        onSubmitQuestion: handleCloneQuestion,
      };
    } else if (variant === 'create') {
      return {
        successText: 'Successfully created question!',
        busyText: 'Creating question...',
        defaultText: 'Create question',
        buttonText: 'Create',
        disabled: false,
        isInitialValid: true,
        onSubmitQuestion: handleCloneQuestion,
      };
    } else {
      return {
        successText: 'Successfully removed question!',
        busyText: 'Removing question...',
        defaultText: 'Remove question',
        buttonText: 'Remove',
        disabled: true,
        isInitialValid: true,
        onSubmitQuestion: handleRemoveQuestion,
      };
    }
  };
  return {
    complianceStandards: complianceStandards.state,
    variantProps,
  };
};

export const mockData = {
  complianceStandards: [] as string[],
  variantProps: mockFunction,
};

export default useMockHook(useVariantQuestionModalLogic, mockData);
